/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { 
  X, Globe, ShieldCheck, ShieldAlert, Sparkles, Plus, 
  Trash2, AlertCircle, Check, ExternalLink, Palette, School,
  Mail, CheckCircle2, Sliders, ArrowUpRight, Copy
} from 'lucide-react';
import { Tenant } from '../../types';
import { useInAppDialog } from '../Common/InAppDialog';

interface PortalCountyData {
  id: string;
  name: string;
  subdomain: string;
  isActive: boolean;
  isVisible: boolean;
  highSchoolActive: boolean;
  postSecondaryActive: boolean;
  foundationsEnabled: boolean;
  prohibitedEmailDomains?: string[];
  tenantMatch?: Tenant;
}

interface PortalConfigModalProps {
  county: PortalCountyData | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateTenant: (tenantId: string, updates: Partial<Tenant>) => void;
  onUpdateAllTenants?: (updates: Partial<Tenant>) => Promise<void>;
  onOpenPersonaEditor?: (county: any) => void;
}

export const PortalConfigModal: React.FC<PortalConfigModalProps> = ({
  county,
  isOpen,
  onClose,
  onUpdateTenant,
  onUpdateAllTenants,
  onOpenPersonaEditor
}) => {
  const { showToast, showConfirm } = useInAppDialog();
  const [newDomain, setNewDomain] = useState('');
  const [copiedSubdomain, setCopiedSubdomain] = useState(false);

  if (!isOpen || !county) return null;

  const prohibitedDomains = county.prohibitedEmailDomains || [];

  const handleAddDomain = (domainToAdd?: string) => {
    const raw = domainToAdd !== undefined ? domainToAdd : newDomain;
    let clean = raw.trim().toLowerCase();
    if (!clean) return;
    
    // Normalize domain string
    if (clean.startsWith('@')) clean = clean.slice(1);
    if (clean.startsWith('*.')) clean = clean.slice(2);
    if (clean.startsWith('.')) clean = clean.slice(1);
    clean = clean.trim();

    if (!clean) return;

    if (prohibitedDomains.includes(clean)) {
      showToast({
        title: 'Domain Already Exists',
        message: `@${clean} is already in the prohibited list for this portal.`,
        type: 'warning'
      });
      setNewDomain('');
      return;
    }

    const updated = [...prohibitedDomains, clean];
    onUpdateTenant(county.id, { prohibitedEmailDomains: updated });
    setNewDomain('');
    showToast({
      title: 'Prohibited Domain Added',
      message: `Students can no longer register using @${clean} email addresses in this portal.`,
      type: 'success'
    });
  };

  const handleRemoveDomain = (domainToRemove: string) => {
    const updated = prohibitedDomains.filter(d => d !== domainToRemove);
    onUpdateTenant(county.id, { prohibitedEmailDomains: updated });
    showToast({
      title: 'Prohibited Domain Removed',
      message: `@${domainToRemove} restriction removed.`,
      type: 'info'
    });
  };

  const handleApplyDomainsToAllPortals = async () => {
    if (!onUpdateAllTenants) return;
    const confirmed = await showConfirm({
      title: 'Apply Prohibited Domains Statewide?',
      message: `Are you sure you want to copy these ${prohibitedDomains.length} prohibited email domain rule(s) to all 46 regional portals across South Carolina?`,
      confirmText: 'Apply Statewide',
      type: 'warning'
    });
    if (confirmed) {
      await onUpdateAllTenants({ prohibitedEmailDomains: prohibitedDomains });
      showToast({
        title: 'Statewide Policy Applied',
        message: 'Prohibited email domains have been updated for all 46 regional portals.',
        type: 'success'
      });
    }
  };

  const handleCopySubdomain = () => {
    const fullDomain = county.subdomain.includes('.') 
      ? county.subdomain 
      : `${county.subdomain}.wbl.sc`;
    navigator.clipboard.writeText(fullDomain);
    setCopiedSubdomain(true);
    setTimeout(() => setCopiedSubdomain(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-3 sm:p-5 overflow-y-auto">
      <div 
        className="bg-white rounded-3xl border border-slate-200 shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200 text-left"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
          <div className="flex items-center space-x-3.5">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center border shadow-3xs ${
              county.isActive 
                ? 'bg-sky-50 border-sky-200 text-[#0094c8]' 
                : 'bg-slate-100 border-slate-200 text-slate-400'
            }`}>
              <Globe className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-black text-[#011d3c] uppercase tracking-tight">
                  {county.name} County Portal
                </h3>
                <span className={`px-2 py-0.5 text-[9px] font-black uppercase rounded-md border ${
                  county.isActive 
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                    : 'bg-slate-100 text-slate-500 border-slate-200'
                }`}>
                  {county.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className={`px-2 py-0.5 text-[9px] font-black uppercase rounded-md border ${
                  county.isVisible 
                    ? 'bg-blue-50 text-blue-700 border-blue-200' 
                    : 'bg-slate-100 text-slate-500 border-slate-200'
                }`}>
                  {county.isVisible ? 'Visible' : 'Hidden'}
                </span>
              </div>
              <p className="text-[10px] text-slate-400 font-extrabold uppercase tracking-widest mt-0.5 flex items-center space-x-2">
                <span>Jurisdiction: {county.name} County, SC</span>
                <span>•</span>
                <span className="font-mono text-[#0094c8]">{county.subdomain}.wbl.sc</span>
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white hover:bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-all cursor-pointer shadow-3xs"
            title="Close Settings"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-xs text-slate-700">
          
          {/* Section 1: Core Availability & Visibility Switches */}
          <div className="bg-slate-50/60 border border-slate-200/80 rounded-2xl p-4.5 space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                Portal Operations & Visibility
              </span>
              <button
                type="button"
                onClick={handleCopySubdomain}
                className="text-[10px] font-black text-[#0094c8] hover:text-[#007ba6] flex items-center space-x-1 cursor-pointer transition-colors"
                title="Copy full portal domain address"
              >
                {copiedSubdomain ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                <span>{copiedSubdomain ? 'Copied Link' : 'Copy Domain'}</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Active Toggle Card */}
              <div className="bg-white border border-slate-200 rounded-xl p-3.5 flex items-center justify-between shadow-3xs">
                <div className="space-y-0.5 pr-3">
                  <div className="text-xs font-black text-slate-900 uppercase tracking-tight">Portal Active</div>
                  <div className="text-[11px] text-slate-500 leading-normal">
                    Allow students, employers, and advisors to sign in and interact with this workspace.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onUpdateTenant(county.id, { isActive: !county.isActive })}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    county.isActive ? 'bg-[#0094c8]' : 'bg-slate-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out ${
                      county.isActive ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Visible Toggle Card */}
              <div className="bg-white border border-slate-200 rounded-xl p-3.5 flex items-center justify-between shadow-3xs">
                <div className="space-y-0.5 pr-3">
                  <div className="text-xs font-black text-slate-900 uppercase tracking-tight">Public Map Visibility</div>
                  <div className="text-[11px] text-slate-500 leading-normal">
                    Display on the interactive South Carolina state map and public portal selector.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onUpdateTenant(county.id, { isVisible: !county.isVisible })}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    county.isVisible ? 'bg-[#0094c8]' : 'bg-slate-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out ${
                      county.isVisible ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Section 2: Education Levels & Curriculum Switches */}
          <div className="space-y-3">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">
              Authorized Education Cohorts & Curriculum
            </span>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* High School */}
              <div className="bg-white border border-slate-200 rounded-xl p-3.5 flex flex-col justify-between shadow-3xs space-y-3">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-slate-900 uppercase tracking-wide">High School</span>
                    <span className={`w-2 h-2 rounded-full ${county.highSchoolActive ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  </div>
                  <p className="text-[10px] text-slate-500 leading-relaxed">
                    9th-12th grade high school students and career centers.
                  </p>
                </div>
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[9px] font-bold text-slate-400 uppercase">
                    {county.highSchoolActive ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    type="button"
                    onClick={() => onUpdateTenant(county.id, { highSchoolActive: !county.highSchoolActive })}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                      county.highSchoolActive ? 'bg-[#0094c8]' : 'bg-slate-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out ${
                        county.highSchoolActive ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Post-Secondary */}
              <div className="bg-white border border-slate-200 rounded-xl p-3.5 flex flex-col justify-between shadow-3xs space-y-3">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-slate-900 uppercase tracking-wide">Post-Secondary</span>
                    <span className={`w-2 h-2 rounded-full ${county.postSecondaryActive ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  </div>
                  <p className="text-[10px] text-slate-500 leading-relaxed">
                    Technical colleges, certifications, and university scholars.
                  </p>
                </div>
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[9px] font-bold text-slate-400 uppercase">
                    {county.postSecondaryActive ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    type="button"
                    onClick={() => onUpdateTenant(county.id, { postSecondaryActive: !county.postSecondaryActive })}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                      county.postSecondaryActive ? 'bg-[#0094c8]' : 'bg-slate-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out ${
                        county.postSecondaryActive ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Foundations Curriculum */}
              <div className="bg-white border border-slate-200 rounded-xl p-3.5 flex flex-col justify-between shadow-3xs space-y-3">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-slate-900 uppercase tracking-wide">Foundations Modules</span>
                    <span className={`w-2 h-2 rounded-full ${county.foundationsEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  </div>
                  <p className="text-[10px] text-slate-500 leading-relaxed">
                    Soft-skills readiness and workplace training modules.
                  </p>
                </div>
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[9px] font-bold text-slate-400 uppercase">
                    {county.foundationsEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    type="button"
                    onClick={() => onUpdateTenant(county.id, { foundationsEnabled: !county.foundationsEnabled })}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                      county.foundationsEnabled ? 'bg-[#0094c8]' : 'bg-slate-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out ${
                        county.foundationsEnabled ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Prohibited Student Email Domains (User Request) */}
          <div className="border border-rose-200/80 bg-rose-50/20 rounded-2xl p-4.5 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-rose-150">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 rounded-xl bg-rose-100/80 text-rose-600 flex items-center justify-center flex-shrink-0 shadow-3xs">
                  <ShieldAlert className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-black uppercase tracking-tight text-slate-900">
                    Prohibited Student Email Domains
                  </h4>
                  <p className="text-[11px] text-slate-500">
                    Prohibit students from signing up with school district or expiring email domains.
                  </p>
                </div>
              </div>

              {prohibitedDomains.length > 0 && onUpdateAllTenants && (
                <button
                  type="button"
                  onClick={handleApplyDomainsToAllPortals}
                  className="px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-rose-700 bg-rose-100/70 hover:bg-rose-200 border border-rose-200 rounded-lg transition-all cursor-pointer shadow-3xs flex items-center space-x-1"
                  title="Copy these prohibited domains to all 46 regional portals"
                >
                  <Globe className="w-3 h-3" />
                  <span>Apply Statewide</span>
                </button>
              )}
            </div>

            {/* Explanatory Policy Callout */}
            <div className="bg-white/80 border border-rose-100 rounded-xl p-3 text-[11px] text-slate-600 leading-relaxed flex items-start space-x-2.5">
              <AlertCircle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-bold text-slate-800">Student Account Protection Policy:</span> School district email accounts (e.g., <code className="px-1 py-0.5 bg-rose-50 border border-rose-200 text-rose-700 rounded font-mono text-[10px]">@k12.sc.us</code> or <code className="px-1 py-0.5 bg-rose-50 border border-rose-200 text-rose-700 rounded font-mono text-[10px]">@spart6.org</code>) are routinely disabled by school districts upon student graduation. Requiring a personal email address (e.g. Gmail, Outlook, iCloud) ensures students keep their credentials, placement histories, and work-based learning certificates permanently.
              </div>
            </div>

            {/* Input to Add Prohibited Domain */}
            <div className="space-y-2">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                    <Mail className="w-3.5 h-3.5" />
                  </div>
                  <input
                    type="text"
                    placeholder="Enter domain (e.g. k12.sc.us, spart6.org, district.edu)..."
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddDomain();
                      }
                    }}
                    className="w-full pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-rose-400 placeholder:text-slate-400 font-mono shadow-3xs"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => handleAddDomain()}
                  disabled={!newDomain.trim()}
                  className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white font-black text-[10px] uppercase tracking-wider rounded-xl transition-all shadow-3xs flex items-center space-x-1.5 cursor-pointer flex-shrink-0"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add Domain</span>
                </button>
              </div>

              {/* Quick Preset Buttons */}
              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase mr-1">Quick Presets:</span>
                <button
                  type="button"
                  onClick={() => handleAddDomain('k12.sc.us')}
                  className="px-2 py-0.5 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg text-[10px] font-medium text-slate-700 transition-colors cursor-pointer"
                >
                  + k12.sc.us (SC Public Schools)
                </button>
                <button
                  type="button"
                  onClick={() => handleAddDomain(`${county.id}.k12.sc.us`)}
                  className="px-2 py-0.5 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg text-[10px] font-medium text-slate-700 transition-colors cursor-pointer"
                >
                  + {county.id}.k12.sc.us
                </button>
                <button
                  type="button"
                  onClick={() => handleAddDomain('edu')}
                  className="px-2 py-0.5 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg text-[10px] font-medium text-slate-700 transition-colors cursor-pointer"
                >
                  + .edu (Higher Ed)
                </button>
              </div>
            </div>

            {/* Active Prohibited Domains List */}
            <div className="space-y-1.5">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide flex items-center justify-between">
                <span>Active Prohibited Domains ({prohibitedDomains.length})</span>
                {prohibitedDomains.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onUpdateTenant(county.id, { prohibitedEmailDomains: [] })}
                    className="text-rose-600 hover:underline cursor-pointer lowercase font-medium"
                  >
                    clear all
                  </button>
                )}
              </div>

              {prohibitedDomains.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-200 rounded-xl p-3 text-center text-slate-400 text-xs">
                  No prohibited email domains configured for this portal yet. Type a domain above to block school district emails.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 pt-1">
                  {prohibitedDomains.map((domain) => (
                    <div 
                      key={domain} 
                      className="bg-white border border-rose-200 text-rose-900 px-2.5 py-1.5 rounded-xl text-xs font-mono flex items-center space-x-2 shadow-3xs group"
                    >
                      <span className="text-rose-400 font-sans font-bold">@</span>
                      <span className="font-semibold">{domain}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveDomain(domain)}
                        className="text-slate-400 hover:text-rose-600 hover:bg-rose-50 p-0.5 rounded-md transition-colors cursor-pointer"
                        title={`Remove restriction on @${domain}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Section 4: Portal Customizer & Persona Sections */}
          <div className="bg-slate-50/50 border border-slate-200/70 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-0.5">
              <div className="text-xs font-black text-slate-900 uppercase tracking-tight flex items-center space-x-1.5">
                <Palette className="w-3.5 h-3.5 text-[#0094c8]" />
                <span>Landing Page Persona Customizer</span>
              </div>
              <p className="text-[11px] text-slate-500">
                Tailor direct sponsor marks, hero banners, and student/employer copy for {county.name} County.
              </p>
            </div>
            {onOpenPersonaEditor && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenPersonaEditor(county);
                }}
                className="px-3 py-2 bg-white hover:bg-slate-100 border border-slate-200 text-[#0094c8] hover:text-[#007ba6] font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition-all shadow-3xs flex items-center justify-center space-x-1.5 cursor-pointer flex-shrink-0"
              >
                <span>Edit Landing Page</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3.5 border-t border-slate-100 flex items-center justify-between bg-slate-50/80">
          <div className="text-[10px] text-slate-400 font-mono">
            ID: <span className="text-slate-700 font-bold">{county.id}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-[#0094c8] hover:bg-[#007ba6] text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-3xs cursor-pointer"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
